import { Button } from "@/ingot";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Pill } from "@/components/ui/Pill";

import { ERROR_CATALOG, type ErrorKind } from "./errorCatalog";

/**
 * The one error screen — 401 / 403 / 404 / 500 / 503, a stale-chunk
 * reload prompt, and the root boundary's catch-all all render this.
 * What differs between them lives in ``errorCatalog``, not here.
 *
 * Deliberately free of router hooks: the root ``ErrorBoundary`` sits
 * OUTSIDE ``<RouterProvider>``, so a ``<Link>`` or ``useParams`` here
 * would throw in exactly the situation this screen exists to handle.
 * Navigation therefore goes through ``window.location`` — on an error
 * screen a hard load is a feature anyway, since it drops the broken
 * client state that got us here.
 *
 * Callers own the chrome. Mounted as a route ``errorElement`` it
 * renders inside the surrounding shell (customer header/footer, or
 * the admin sidebar), because React Router renders it at the failing
 * route's position in the tree.
 */

export interface ErrorPageDetail {
  message?: string;
  stack?: string;
  route?: string;
  /** Sentry event id — absent when the user declined analytics consent. */
  eventId?: string;
}

interface ErrorPageProps {
  kind: ErrorKind;
  detail?: ErrorPageDetail;
  /** Slot between the error code and the technical details. */
  children?: ReactNode;
}

const BUILD_ID = import.meta.env.VITE_APP_VERSION ?? "dev";

export function ErrorPage({
  kind,
  detail,
  children,
}: ErrorPageProps): JSX.Element {
  const { t } = useTranslation("common");
  const spec = ERROR_CATALOG[kind];

  const eyebrow =
    spec.status !== null
      ? String(spec.status)
      : spec.eyebrowKey
        ? t(spec.eyebrowKey)
        : null;

  function reload(): void {
    window.location.reload();
  }

  function goHome(): void {
    window.location.href = "/";
  }

  return (
    <main
      className="mx-auto flex max-w-md flex-col items-center px-6 py-20 text-center"
      data-testid="error-page"
      data-error-kind={kind}
    >
      {eyebrow && <Pill tone={spec.tone}>{eyebrow}</Pill>}
      <h1 className="mt-4 text-2xl font-bold text-ink">{t(spec.headingKey)}</h1>
      <p className="mt-3 text-ink-2">{t(spec.leadKey)}</p>

      <div className="mt-6 flex gap-3">
        <Button
          variant={spec.primary === "reload" ? "primary" : "secondary"}
          onClick={reload}
          data-testid="error-page-reload"
        >
          {t("errorBoundary.reload")}
        </Button>
        <Button
          variant={spec.primary === "home" ? "primary" : "secondary"}
          onClick={goHome}
          data-testid="error-page-home"
        >
          {t("errorBoundary.home")}
        </Button>
      </div>

      {detail?.eventId && (
        <p className="mono mt-5 text-xs text-ink-3" data-testid="error-page-code">
          {t("errorPage.code", { code: detail.eventId })}
        </p>
      )}

      {children}

      <TechnicalDetails kind={kind} detail={detail} />
    </main>
  );
}

/**
 * Collapsed in production, open in dev. Shipping it to customers is
 * the point: a support ticket that arrives with a build id and the
 * real message beats one that says "it broke". Nothing here is
 * privileged — it's the same data the browser console already shows.
 */
function TechnicalDetails({
  kind,
  detail,
}: {
  kind: ErrorKind;
  detail?: ErrorPageDetail;
}): JSX.Element | null {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);

  const rows: [string, string][] = [["kind", kind]];
  if (detail?.message) rows.push(["error", detail.message]);
  if (detail?.route) rows.push(["route", detail.route]);
  rows.push(["build", BUILD_ID]);
  if (detail?.eventId) rows.push(["sentry", detail.eventId]);

  async function copyReport(): Promise<void> {
    const report = [
      ...rows.map(([k, v]) => `${k}: ${v}`),
      `time: ${new Date().toISOString()}`,
      `ua: ${navigator.userAgent}`,
      ...(detail?.stack ? ["", detail.stack] : []),
    ].join("\n");
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
    } catch {
      /* clipboard blocked (insecure origin / denied) — leave the label alone */
    }
  }

  return (
    <details
      className="mt-8 w-full text-left"
      open={import.meta.env.DEV}
      data-testid="error-page-details"
    >
      <summary className="cursor-pointer text-xs text-ink-3 hover:text-ink-2">
        {t("errorPage.details.summary")}
      </summary>
      <dl className="mono mt-2 overflow-x-auto rounded-md bg-surface-2 p-3 text-[11.5px] leading-relaxed text-ink-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="shrink-0 text-ink-3">{k}</dt>
            <dd className="break-all">{v}</dd>
          </div>
        ))}
      </dl>
      <Button
        variant="secondary"
        size="sm"
        className="mt-2"
        onClick={() => void copyReport()}
        data-testid="error-page-copy"
      >
        {t(copied ? "errorPage.details.copied" : "errorPage.details.copy")}
      </Button>
    </details>
  );
}
